import { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Lock, Mail, AlertTriangle, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { apiClient } from '../../services/api';

interface ResetPasswordModalProps {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    orgId?: string;
}

export default function ResetPasswordModal({ user, isOpen, onClose, onSuccess, orgId }: ResetPasswordModalProps) {
    const [sendEmail, setSendEmail] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [temporaryPassword, setTemporaryPassword] = useState('');
    const [showTempPassword, setShowTempPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const handleReset = async () => {
        setLoading(true);
        setError('');

        try {
            const response = orgId
                ? await apiClient.resetOrgUserPassword(orgId, user.id, { sendEmail })
                : await apiClient.resetUserPassword(user.id, { sendEmail });

            if (!sendEmail && response.temporaryPassword) {
                // Show temporary password modal
                setTemporaryPassword(response.temporaryPassword);
                setShowTempPassword(true);
            } else {
                // Email sent - close and refresh
                toast.success('Password reset email sent successfully');
                onSuccess();
                onClose();
                resetState();
            }
        } catch (err: any) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(temporaryPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleComplete = () => {
        onSuccess();
        onClose();
        resetState();
    };

    const resetState = () => {
        setSendEmail(false);
        setError('');
        setTemporaryPassword('');
        setShowTempPassword(false);
        setShowPassword(false);
        setCopied(false);
    };

    const handleClose = () => {
        onClose();
        resetState();
    };

    // Temporary Password Display Modal
    if (showTempPassword) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                                <Lock className="w-5 h-5" style={{ color: '#F59E0B' }} />
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900">Temporary Password</h2>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        {/* Warning */}
                        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-900">Important</p>
                                <p className="text-sm text-amber-700 mt-1">
                                    Copy this password now. It will not be shown again. The user must change this password on their next login.
                                </p>
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Password reset for:</p>
                            <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                        </div>

                        {/* Password Display */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Temporary Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={temporaryPassword}
                                    readOnly
                                    className="w-full px-4 py-3 pr-24 border border-gray-300 rounded-lg bg-gray-50 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                />
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                        title={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                    <div className="group relative inline-flex">
                                        <button
                                            type="button"
                                            onClick={handleCopyPassword}
                                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                                        </button>
                                        <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">{copied ? 'Copied!' : 'Copy password'}</span>
                                    </div>
                                </div>
                            </div>
                            {copied && (
                                <p className="text-sm text-green-600 mt-2">Password copied to clipboard!</p>
                            )}
                        </div>

                        {/* Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm font-medium text-blue-900 mb-2">Next Steps:</p>
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                                <li>Copy and securely share this password with the user</li>
                                <li>User logs in with their email and this temporary password</li>
                                <li>User will be forced to create a new password</li>
                            </ol>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                        <button
                            onClick={handleComplete}
                            className="px-6 py-2 text-white rounded-lg font-medium transition-colors"
                            style={{ backgroundColor: '#F59E0B' }}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Main Reset Password Modal
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                            <Lock className="w-5 h-5" style={{ color: '#F59E0B' }} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* User Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Reset password for:</p>
                        <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                    </div>

                    {/* Warning */}
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-amber-900">This will generate a new temporary password</p>
                            <p className="text-sm text-amber-700 mt-1">
                                The user's current password will be replaced and they will be required to change it on their next login.
                            </p>
                        </div>
                    </div>

                    {/* Delivery Method */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            How would you like to deliver the temporary password?
                        </label>
                        <div className="space-y-3">
                            {/* Manual Delivery Option */}
                            <label className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-amber-500 transition-colors">
                                <input
                                    type="radio"
                                    name="delivery"
                                    checked={!sendEmail}
                                    onChange={() => setSendEmail(false)}
                                    className="mt-1"
                                    style={{ accentColor: '#F59E0B' }}
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Lock className="w-4 h-4 text-gray-600" />
                                        <span className="font-medium text-gray-900">Display password on screen</span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Copy the temporary password and share it securely with the user
                                    </p>
                                </div>
                            </label>

                            {/* Email Delivery Option */}
                            <label className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-amber-500 transition-colors">
                                <input
                                    type="radio"
                                    name="delivery"
                                    checked={sendEmail}
                                    onChange={() => setSendEmail(true)}
                                    className="mt-1"
                                    style={{ accentColor: '#F59E0B' }}
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Mail className="w-4 h-4 text-gray-600" />
                                        <span className="font-medium text-gray-900">Send via email</span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Automatically email the temporary password to {user.email}
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleReset}
                        disabled={loading}
                        className="px-6 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#F59E0B' }}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                Resetting...
                            </span>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}